import React, { useRef, useState, useEffect, useCallback } from "react";
import * as Progress from "react-native-progress";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  FlatList,
  Pressable,
  SectionList,
  ActivityIndicator,
} from "react-native";
import debounce from "lodash/debounce";
import { useDispatch, useSelector } from "react-redux";

// Custom Imports
import color from "@constants/color";
import { scale } from "@utils/scale";
import t from "@constants/localization";
import { width } from "@constants/metrics";
import BackArrow from "@assets/svg/BackArrow";
import CloseIcon from "@assets/svg/CloseIcon";
import SearchBar from "@components/SearchBar";
import CustomText from "@components/CustomText";
import SearchHome from "@assets/svg/SearchHome";
import RightArrow from "@assets/svg/RightArrow";
import { contestSearchTerm } from "@services/api";
import contestAction from "@actions/contest.action";
import EmptySearch from "@assets/svg/Contest/EmptySearch";
import CircularLoader from "@screens/Story/CircularLoader";
import { CONTEST_EVENTS } from "@constants/analyticsEventsCT";
import {
  moreCustLoadingSelector,
  myCustomersSelector,
} from "@selectors/contest.selector";
import AlreadyOrderedCustomers from "../Components/AlreadyOrderedCustomers";
import OrderedCard from "../Components/OrderedCard";
import contestEvents from "../ContestEvents";

const PATH = "screens/Contest";
const appLinkSelector = (state) => state?.auth?.user?.app_share_app_link;

function MyCustomers({ navigation, route }) {
  const searchref = useRef(null);
  const { contest_id = 0 } = route?.params || {};
  const [orderItem, setOrderItem] = useState({});
  const [searchData, setSearchData] = useState([]);
  const [query, setQueryChangeTerm] = useState("");
  const [orderModal, setOrderModal] = useState(false);

  const dispatch = useDispatch();
  const { data = {}, api = {} } = useSelector(myCustomersSelector);
  const app_share_link = useSelector(appLinkSelector);
  const moreCustomerLoading = useSelector(moreCustLoadingSelector);
  const { pending = false } = api || {};
  const {
    contestLeaderCustomers = [],
    orderedCx = 0,
    totalCx = 0,
  } = data || {};
  const unOrderedCX = totalCx - orderedCx;

  const orderedEnHi = t(`${PATH}/ordered`);
  const orderToBePlaced = t(`${PATH}/ordersPlaced`);

  useEffect(() => {
    dispatch(
      contestAction.getMyCustomersContest.pending(
        { contest_id, limit: 10, offset: 0 },
        null,
      ),
    );
  }, []);

  const onBackClick = () => {
    navigation.goBack();
  };

  const onFocus = () => {
    contestEvents(CONTEST_EVENTS.SEARCH_CLICKED, {
      screen: "Contest task page",
    });
  };

  const handleQueryChange = (searchTerm = "", keywordClicked) => {
    setQueryChangeTerm(searchTerm);
    const trimedSearchTerm = searchTerm.trim();
    if (trimedSearchTerm.length >= 2) {
      searchCustomers(trimedSearchTerm);
    }
  };

  const orderedCard = () => {
    contestEvents(CONTEST_EVENTS.ORDERS_PLACED_CLICKED, {
      screen: "Contest task page",
      total_customer_with_order: orderedCx,
      total_customer_count: totalCx,
    });
    navigation.navigate("OrderedList", { contest_id, orderedCx });
  };

  const onPressRightIcon = () => {
    searchref.current.clear();
    setSearchData({});
    handleQueryChange("");
  };

  const handleOrderDetailsModal = (flag) => {
    setOrderModal(flag);
    setOrderItem({});
  };

  const onPressOrder = (item) => {
    setOrderItem(item);
    setOrderModal(true);
  };

  const loadMore = useCallback(async () => {
    if (contestLeaderCustomers.length === 0) {
      return;
    }
    if (contestLeaderCustomers.length >= unOrderedCX) {
      return;
    }
    try {
      if (unOrderedCX > contestLeaderCustomers.length) {
        dispatch(contestAction.updateMoreCustomerLoading(true));
        dispatch(
          contestAction.getMyCustomersContest.pending(
            { contest_id, limit: 10, offset: contestLeaderCustomers.length },
            null,
          ),
        );
      }
    } catch (e) {
      console.log(e);
      dispatch(contestAction.updateMoreCustomerLoading(false));
    }
  }, [contestLeaderCustomers, totalCx, orderedCx, unOrderedCX]);

  const searchCustomers = React.useCallback(
    debounce(function (searchText) {
      contestSearchTerm({
        contest_id,
        searchTerm: searchText,
      })
        .then((response) => {
          const { data } = response;
          const final_search_data = [];
          if (
            Array.isArray(data?.notOrdered) &&
            data?.notOrdered.length !== 0
          ) {
            final_search_data.push({
              title: orderToBePlaced,
              data: data?.notOrdered,
            });
          }
          if (Array.isArray(data?.ordered) && data.ordered.length !== 0) {
            final_search_data.push({
              title: orderedEnHi,
              data: data?.ordered,
            });
          }
          setSearchData(final_search_data);
          contestEvents(CONTEST_EVENTS.CUSTOMER_NAME_SEARCH, {
            screen: "Contest task page",
            searchTerm: searchText,
          });
        })
        .catch((error) => {
          if (error.response) {
            console.log("searchCustomers error", error.response);
            setSearchData([]);
          }
        });
    }, 400),
    [],
  );

  const searchDataTitle = ({ section: { title = "" } }) => {
    return (
      <View>
        <CustomText
          variant="title_18_semi"
          newfont={true}
          numberOfLines={1}
          style={styles.orderedPlacedText}
        >
          {title}
        </CustomText>
      </View>
    );
  };

  const renderContestItem = ({ item, index, section: { title = "" } }) => {
    if (item && title === orderToBePlaced) {
      return (
        <OrderedCard
          contest_id={contest_id}
          navigation={navigation}
          index={index}
          task={item}
          onPressOrder={onPressOrder}
          app_link={app_share_link}
          trackEvent={contestEvents}
        />
      );
    } else if (item && title === orderedEnHi) {
      return (
        <AlreadyOrderedCustomers
          item={item}
          index={index}
          onPressOrder={onPressOrder}
          contestEvents={contestEvents}
          navigation={navigation}
        />
      );
    } else return null;
  };

  function ContestSearchComponent() {
    if (
      Array.isArray(searchData) &&
      searchData.length !== 0 &&
      query.trim().length >= 2
    ) {
      return (
        <SectionList
          removeClippedSubviews
          sections={searchData}
          keyExtractor={(item, index) => `${index}`}
          renderItem={renderContestItem}
          renderSectionHeader={searchDataTitle}
          style={styles.listContainer}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
        />
      );
    } else if (query.trim().length >= 2) {
      return <ContestEmptyComponent />;
    }
    return <></>;
  }

  const customerDataFooter = () => {
    return (
      <>
        {moreCustomerLoading ? (
          <ActivityIndicator size="large" color={color.primary} />
        ) : null}
      </>
    );
  };

  const _keyExtractor = (item, index) => `${index}`;
  const _renderItem = ({ item, index }) => {
    if (item) {
      return (
        <OrderedCard
          contest_id={contest_id}
          navigation={navigation}
          index={index}
          task={item}
          onPressOrder={onPressOrder}
          app_link={app_share_link}
          trackEvent={contestEvents}
        />
      );
    } else return null;
  };

  const showListHeader = () => {
    return (
      <View style={styles.scrollViewContainer}>
        <Pressable onPress={orderedCard} style={styles.orderedContainer}>
          <View style={styles.orderedTextContainer}>
            <CustomText
              variant="subtitle_16"
              newfont={true}
              numberOfLines={1}
              style={styles.orderedText}
            >
              {t(`${PATH}/ordered`)}
            </CustomText>
            <View style={styles.nextIconContainer}>
              <RightArrow fill={color.primary} stroke={color.primary} />
            </View>
          </View>
          <Progress.Bar
            height={8}
            color={color.darkSkyBlue}
            width={width - 101}
            progress={
              parseInt(totalCx) === 0
                ? 0
                : parseInt(orderedCx) / parseInt(totalCx)
            }
            borderWidth={0}
            borderRadius={10}
            style={styles.progressBar}
          />
          <CustomText
            variant="body_text_4"
            newfont={true}
            numberOfLines={1}
            style={styles.description}
          >
            {`${orderedCx} / ${totalCx}`}
          </CustomText>
        </Pressable>
        {contestLeaderCustomers.length !== 0 ? (
          <CustomText
            variant="title_18_semi"
            newfont={true}
            numberOfLines={1}
            style={styles.orderedPlacedText}
          >
            {t(`${PATH}/ordersPlaced`)}
          </CustomText>
        ) : null}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerTitleContainer}>
        <TouchableOpacity activeOpacity={0.8} onPress={onBackClick}>
          <BackArrow />
        </TouchableOpacity>
        <CustomText
          variant="title_20"
          newfont={true}
          numberOfLines={1}
          style={styles.textLeftPadding}
        >
          {t(`${PATH}/myCustomers`)}
        </CustomText>
      </View>
      <SearchBar
        ref={searchref}
        placeholder={t(`${PATH}/search`)}
        query={query}
        handleQueryChange={handleQueryChange}
        shouldShowIcon={true}
        rightIcon={!query ? <SearchHome /> : <CloseIcon />}
        clearSearch={true}
        disabled
        onPressRightIcon={onPressRightIcon}
        customStyles={styles.searchContainer}
        onFocus={onFocus}
      />
      {!moreCustomerLoading && pending ? (
        <View style={styles.loaderStyle}>
          <CircularLoader />
        </View>
      ) : query.trim().length >= 2 ? (
        <ContestSearchComponent />
      ) : (
        <FlatList
          renderAheadOffset={10}
          data={contestLeaderCustomers}
          renderItem={_renderItem}
          keyExtractor={_keyExtractor}
          showsVerticalScrollIndicator={false}
          style={styles.listContainer}
          ListHeaderComponent={showListHeader}
          contentContainerStyle={styles.contentContainer}
          onEndReached={loadMore}
          ListFooterComponent={customerDataFooter}
        />
      )}
    </View>
  );
}

export default MyCustomers;

function ContestEmptyComponent() {
  return (
    <View style={styles.emptyListContainer}>
      <EmptySearch />
      <CustomText
        variant="body_text_1"
        newfont={true}
        numberOfLines={1}
        style={styles.searchText}
      >
        {t(`${PATH}/emptySearch`)}
      </CustomText>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  headerTitleContainer: {
    alignItems: "center",
    flexDirection: "row",
    height: 56,
    paddingHorizontal: 16,
  },
  textLeftPadding: {
    paddingLeft: scale(23),
    color: color.mediumBlack,
  },
  searchContainer: {
    marginTop: 10,
    marginHorizontal: 16,
  },
  placeholder: {
    marginLeft: 8,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(0,0,0,0.36)",
  },
  scrollViewContainer: {
    marginTop: 23,
  },
  orderedContainer: {
    flexDirection: "column",
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
    borderRadius: scale(10),
    backgroundColor: color.white,
    padding: 16,
  },
  orderedText: {
    color: color.mediumBlack,
  },
  nextIconContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "rgba(228, 0, 70, 0.05)",
    justifyContent: "center",
    alignItems: "center",
  },
  orderedTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  progressBar: {
    backgroundColor: color.veryLightSkyBlue,
    marginTop: 6,
  },
  description: {
    color: color.mediumBlack,
    marginTop: 10,
  },
  orderedPlacedText: {
    color: color.mediumBlack,
    marginTop: 23,
    marginBottom: 9,
  },
  listContainer: {
    marginTop: 9,
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: 20,
    backgroundColor: color.white,
    paddingHorizontal: 16,
  },
  loaderStyle: {
    position: "absolute",
    alignSelf: "center",
    zIndex: 100,
  },
  emptyListContainer: {
    flex: 1,
    alignItems: "center",
    paddingTop: 150,
  },
  searchText: {
    color: color.mediumBlack,
    marginTop: 30,
  },
});
